import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import GavelIcon from '@mui/icons-material/Gavel';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Fab, Grid, IconButton, List, ListItem, ListItemButton, ListItemText, Snackbar, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useNavigate } from "react-router-dom";
import AcceptTerms from './AcceptTerms';
import './App.css';
import Footer from './Footer';
import './Home.css';
import LawAppBar from './LawAppBar';
import LoginScreen from './LoginScreen';
import { handleGoogleLoginSuccess, logout as logoutUtil, renderGoogleLoginButton } from './LoginUtils';

export default function MyCases() {
    const [cookies, setCookie, removeCookie] = useCookies(['given_name', 'userid', 'picture', 'email', 'credential']);

    const [cases, setCases] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [termsAcceptanceRequired, setTermsAcceptanceRequired] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [caseToDelete, setCaseToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState(null);
    const [processingErrorDialog, setProcessingErrorDialog] = useState(null);
    const navigate = useNavigate();

    function onGoogleLoginSuccess(response) {
        handleGoogleLoginSuccess(response, setCookie, () => {
            setIsLoading(true);
            fetchCases();
        });
    }

    function renderLoginButton() {
        renderGoogleLoginButton("googleLogin", onGoogleLoginSuccess);
    }

    function logout() {
        logoutUtil(removeCookie, renderLoginButton);
    }

    function fetchCases() {
        fetch('/api/cases', {
            method: 'GET',
            credentials: 'same-origin'
        })
            .then(res => {
                if (res.status === 401) {
                    logout();
                    return { cases: {} };
                }
                if (res.status === 403) {
                    const termsHeader = res.headers.get('X-Terms-Acceptance-Required');
                    if (termsHeader === 'new-terms' || termsHeader === 'new-user') {
                        setTermsAcceptanceRequired(termsHeader);
                        setIsLoading(false);
                        return { cases: {} };
                    }
                }
                return res.json();
            })
            .then(json => {
                setCases(json['cases'] || {});
                setIsLoading(false);
            })
            .catch(err => {
                console.log('Unable to load cases: ' + err);
                setIsLoading(false);
            });
    }

    function handleTermsAccepted() {
        setTermsAcceptanceRequired(null);
        setIsLoading(true);
        fetchCases();
    }

    useEffect(() => {
        if (cookies.credential && cookies.userid) {
            setIsLoading(true);
            fetchCases();
            return;
        }
        // LoginScreen component handles button rendering
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onCaseSelected = function (caseid) {
        navigate('/case/' + caseid);
    }

    const handleDeleteClick = (e, caseid) => {
        e.stopPropagation(); // Prevent navigation when clicking delete
        setCaseToDelete(caseid);
        setDeleteDialogOpen(true);
    }

    const handleDeleteConfirm = async () => {
        if (!caseToDelete) return;

        setIsDeleting(true);
        try {
            const response = await fetch(`/api/case/${caseToDelete}`, {
                method: 'DELETE',
                credentials: 'same-origin'
            });

            if (response.status === 401) {
                logout();
                return;
            }

            if (response.ok) {
                // Remove case from local state
                const updatedCases = { ...cases };
                delete updatedCases[caseToDelete];
                setCases(updatedCases);
                setSnackbarMessage('Case deleted successfully');
            } else if (response.status === 409) {
                // Processing in progress error - show dialog with title and message
                try {
                    const errorData = await response.json();
                    setProcessingErrorDialog({
                        title: errorData.title || 'Processing in Progress',
                        message: errorData.message || 'This case is actively being processed. Please retry after a few minutes until all processing is complete to ensure all case data is deleted.'
                    });
                } catch (parseErr) {
                    const errorText = await response.text();
                    setSnackbarMessage('Error deleting case: ' + errorText);
                }
            } else {
                const errorText = await response.text();
                setSnackbarMessage('Error deleting case: ' + errorText);
            }
        } catch (err) {
            console.error('Error deleting case:', err);
            setSnackbarMessage('Error deleting case: ' + err.message);
        } finally {
            setIsDeleting(false);
            setDeleteDialogOpen(false);
            setCaseToDelete(null);
        }
    }

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setCaseToDelete(null);
    }

    const caseItems = Object.keys(cases).reverse().map(caseid =>
        <ListItem
            key={caseid}
            className="case-item"
            sx={{
                padding: '0',
                display: 'flex',
                alignItems: 'center'
            }}
        >
            <ListItemButton
                onClick={() => onCaseSelected(caseid)}
                sx={{ flex: 1 }}
            >
                <GavelIcon className="icon-style" />
                <ListItemText primary={cases[caseid]['name'] || caseid} />
            </ListItemButton>
            <IconButton
                edge="end"
                aria-label="delete"
                onClick={(e) => handleDeleteClick(e, caseid)}
                disabled={isDeleting && caseToDelete === caseid}
                sx={{
                    marginRight: '8px',
                    color: 'var(--text-secondary)',
                    '&:hover': {
                        color: 'error.main'
                    }
                }}
            >
                {isDeleting && caseToDelete === caseid ? (
                    <img
                        src="/loading.gif"
                        alt="deleting..."
                        style={{
                            height: "20px",
                            width: "20px",
                            filter: 'opacity(0.8)'
                        }}
                    />
                ) : (
                    <DeleteIcon />
                )}
            </IconButton>
        </ListItem>
    );

    // Show login screen if not authenticated or terms acceptance required
    if (cookies.credential === undefined || cookies.credential === null) {
        return (
            <LoginScreen onGoogleLoginSuccess={onGoogleLoginSuccess} />
        );
    }

    if (termsAcceptanceRequired) {
        return (
            <AcceptTerms
                termsType={termsAcceptanceRequired}
                onTermsAccepted={handleTermsAccepted}
            />
        );
    }

    // Show loading state while fetching cases
    if (isLoading) {
        return (
            <div className="App">
                <LawAppBar cookies={cookies} />
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: '100vh',
                    background: 'var(--background-color)'
                }}>
                    <img
                        src="/loading.gif"
                        alt="loading..."
                        style={{
                            height: "50px",
                            filter: 'opacity(0.8)'
                        }}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="App">
            <LawAppBar cookies={cookies} />
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 'calc(100vh - 48px)'
            }}>
                <Box sx={{
                    flex: 1,
                    width: '100%'
                }}>
                    <div className="case-container">
                        <Grid container spacing={0} columns={3} sx={{ marginBottom: 2 }}>
                            <Grid item xs={1} sx={{ textAlign: "left" }}>
                                <Fab
                                    variant="extended"
                                    size="medium"
                                    color="primary"
                                    onClick={() => navigate('/case/new')}
                                    sx={{
                                        top: "-4px",
                                        background: 'var(--button-gradient)',
                                        boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontSize: '0.95rem',
                                        padding: '14px 24px',
                                        borderRadius: '12px',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        border: 'none',
                                        whiteSpace: 'nowrap',
                                        '&:hover': {
                                            transform: 'translateY(-3px)',
                                            boxShadow: '0 8px 25px rgba(212, 175, 55, 0.5)',
                                            background: 'var(--button-gradient-hover)'
                                        },
                                        '&:active': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)'
                                        },
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: '-100%',
                                            width: '100%',
                                            height: '100%',
                                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                            transition: 'left 0.6s'
                                        },
                                        '&:hover::before': {
                                            left: '100%'
                                        }
                                    }}
                                >
                                    <AddIcon sx={{ mr: 1 }} />
                                    New Case
                                </Fab>
                            </Grid>
                            <Grid item xs={1}>
                                <Typography
                                    variant="h6"
                                    className="title"
                                    sx={{
                                        padding: "0 0 15px",
                                        color: 'var(--text-primary)',
                                        fontWeight: 500,
                                        fontSize: '1.3rem',
                                        letterSpacing: '-0.01em'
                                    }}
                                >
                                    My Cases
                                </Typography>
                            </Grid>
                        </Grid>
                        {Object.keys(cases).length === 0 ? (
                            <Box sx={{
                                padding: '40px 20px',
                                textAlign: 'center',
                                color: 'var(--text-secondary)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1
                            }}>
                                <GavelIcon sx={{ fontSize: '48px', opacity: 0.3, mb: 1 }} />
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    No cases yet
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    Your cases will appear here once you create them
                                </Typography>
                            </Box>
                        ) : (
                            <List className="list-style" sx={{ padding: '0' }}>
                                {caseItems}
                            </List>
                        )}
                    </div>
                </Box>
                <Footer />
            </Box>
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Delete Case?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        This action cannot be undone and will permanently delete all case data, documents, and analysis.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={isDeleting}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: "center" }}
                open={snackbarMessage !== null}
                autoHideDuration={3000}
                onClose={() => setSnackbarMessage(null)}
                message={snackbarMessage}
                sx={{
                    '& .MuiSnackbarContent-message': {
                        textAlign: 'center',
                        width: '100%'
                    }
                }}
            />
            <Dialog
                open={processingErrorDialog !== null}
                onClose={() => setProcessingErrorDialog(null)}
                aria-labelledby="processing-error-dialog-title"
                aria-describedby="processing-error-dialog-description"
            >
                <DialogTitle id="processing-error-dialog-title">
                    {processingErrorDialog?.title || 'Processing in Progress'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="processing-error-dialog-description">
                        {processingErrorDialog?.message || 'This case is actively being processed. Please retry after a few minutes until all processing is complete to ensure all case data is deleted.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setProcessingErrorDialog(null)} variant="contained">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
